import React, { useState, useMemo, useEffect } from 'react';
import { User, Calendar, AlertCircle, Send } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { RatingScale } from './RatingScale';
import { useMeusColaboradores } from '../../usuarios/hooks';
import { useCriarFeedback } from '../hooks';
import { publishFeedback, atualizarFeedback } from '../api';
import { useFeedbackDraft } from '../hooks/useFeedbackDraft';
import { getActivePeriods } from '../api/evaluationPeriods';
import type { EvaluationPeriod } from '../api/evaluationPeriods';
import { authService } from '../../../shared/lib/auth';
import type { Feedback } from '../../../shared/lib/api';
import type { SelectOption } from '../../../shared/components/AutocompleteSelect';

const MONTH_LABELS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function generatePeriodOptions(startDate: Date, currentDate: Date): SelectOption[] {
  const options: SelectOption[] = [];
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const cursor = new Date(end);
  while (cursor >= start) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const value = `${y}-${String(m + 1).padStart(2, '0')}`;
    const label = `${MONTH_LABELS[m]}/${y}`;
    options.push({ value, label });
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return options;
}

interface DimensionState {
  rating: number | null;
  comment: string;
}

interface FormState {
  collaboratorId: string;
  referencePeriod: string;
  behavior: DimensionState;
  deliveries: DimensionState;
  quality: DimensionState;
  collaboration: DimensionState;
  punctuality: DimensionState;
  strengths: string;
  improvements: string;
  recommendations: string;
}

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  initialCollaboratorId?: string;
  initialDraft?: Feedback;
}

const emptyDimension = (): DimensionState => ({ rating: null, comment: '' });

const DIMENSIONS = [
  { key: 'behavior', label: 'Comportamento e Atitude' },
  { key: 'deliveries', label: 'Entregas e Resultados' },
  { key: 'quality', label: 'Qualidade das Atividades' },
  { key: 'collaboration', label: 'Colaboração e Trabalho em Equipe' },
  { key: 'punctuality', label: 'Pontualidade e Compromisso com Prazos' },
] as const;

export const FeedbackForm: React.FC<Props> = ({ onSuccess, onCancel, initialCollaboratorId, initialDraft }) => {
  const currentUser = authService.getCurrentUser();
  const { data: colaboradores = [] } = useMeusColaboradores();
  const criarFeedback = useCriarFeedback();
  const { scheduleSave, getDraftId, saveStatus, savedAt, resetDraft } = useFeedbackDraft(initialDraft?.id);

  const [form, setForm] = useState<FormState>(() => {
    if (initialDraft) {
      return {
        collaboratorId: initialDraft.collaboratorId,
        referencePeriod: initialDraft.referencePeriod,
        behavior: { rating: initialDraft.behaviorRating ?? null, comment: initialDraft.behaviorComment ?? '' },
        deliveries: { rating: initialDraft.deliveriesRating ?? null, comment: initialDraft.deliveriesComment ?? '' },
        quality: { rating: initialDraft.qualityRating ?? null, comment: initialDraft.qualityComment ?? '' },
        collaboration: { rating: initialDraft.collaborationRating ?? null, comment: initialDraft.collaborationComment ?? '' },
        punctuality: { rating: initialDraft.punctualityRating ?? null, comment: initialDraft.punctualityComment ?? '' },
        strengths: initialDraft.strengths ?? '',
        improvements: initialDraft.improvements ?? '',
        recommendations: initialDraft.recommendations ?? '',
      };
    }
    return {
      collaboratorId: initialCollaboratorId ?? '',
      referencePeriod: '',
      behavior: emptyDimension(),
      deliveries: emptyDimension(),
      quality: emptyDimension(),
      collaboration: emptyDimension(),
      punctuality: emptyDimension(),
      strengths: '',
      improvements: '',
      recommendations: '',
    };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [activePeriods, setActivePeriods] = useState<EvaluationPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(initialDraft?.evaluationPeriodId ?? '');

  useEffect(() => {
    getActivePeriods().then(setActivePeriods).catch(() => {});
  }, []);

  const buildDraftFields = () => ({
    collaboratorId: form.collaboratorId,
    referencePeriod: form.referencePeriod,
    behaviorRating: form.behavior.rating ?? 0,
    behaviorComment: form.behavior.comment || undefined,
    deliveriesRating: form.deliveries.rating ?? 0,
    deliveriesComment: form.deliveries.comment || undefined,
    qualityRating: form.quality.rating ?? 0,
    qualityComment: form.quality.comment || undefined,
    collaborationRating: form.collaboration.rating ?? 0,
    collaborationComment: form.collaboration.comment || undefined,
    punctualityRating: form.punctuality.rating ?? 0,
    punctualityComment: form.punctuality.comment || undefined,
    strengths: form.strengths || undefined,
    improvements: form.improvements || undefined,
    recommendations: form.recommendations || undefined,
    evaluationPeriodId: selectedPeriodId || undefined,
  });

  // POST de criação precisa de managerId; PATCH de atualização não aceita managerId
  const buildCreatePayload = () => ({ ...buildDraftFields(), managerId: currentUser!.id });

  useEffect(() => {
    if (!form.collaboratorId || !form.referencePeriod) return;
    scheduleSave(buildDraftFields());
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const colaboradorOptions = colaboradores.map(c => ({ value: c.id, label: c.name }));

  const selectedCollaborator = useMemo(
    () => colaboradores.find(c => c.id === form.collaboratorId),
    [colaboradores, form.collaboratorId],
  );

  const periodOptions = useMemo(() => {
    if (!selectedCollaborator?.createdAt) return [];
    const start = new Date(selectedCollaborator.createdAt);
    return generatePeriodOptions(start, new Date());
  }, [selectedCollaborator]);

  const handleCollaboratorChange = (id: string) => {
    setForm(f => ({ ...f, collaboratorId: id, referencePeriod: '' }));
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.collaboratorId) e.collaboratorId = 'Selecione um colaborador';
    if (!form.referencePeriod) e.referencePeriod = 'Selecione o período de referência';
    DIMENSIONS.forEach(d => {
      if (!form[d.key].rating) e[d.key] = 'Nota obrigatória';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const existingId = getDraftId();
      if (existingId) {
        await atualizarFeedback(existingId, buildDraftFields());
      } else {
        await criarFeedback.mutateAsync(buildCreatePayload());
      }
      resetDraft();
      onSuccess();
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || err?.message || 'Erro ao salvar feedback.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!validate()) return;
    const draftId = getDraftId();
    if (!draftId) {
      setErrors({ submit: 'Salve o rascunho antes de publicar.' });
      return;
    }
    setIsPublishing(true);
    try {
      await publishFeedback(draftId);
      resetDraft();
      onSuccess();
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || err?.message || 'Erro ao publicar feedback.' });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <form onSubmit={handleSaveDraft} className="space-y-6">
      {/* Cabeçalho */}
      <div className="grid grid-cols-2 gap-4">
        <AutocompleteSelect
          label="Colaborador *"
          icon={<User className="w-5 h-5" />}
          options={colaboradorOptions}
          value={form.collaboratorId}
          onChange={handleCollaboratorChange}
          error={errors.collaboratorId}
        />
        <AutocompleteSelect
          label="Período de Referência *"
          icon={<Calendar className="w-5 h-5" />}
          options={periodOptions}
          value={form.referencePeriod}
          onChange={v => setForm(f => ({ ...f, referencePeriod: v }))}
          error={errors.referencePeriod}
          disabled={!form.collaboratorId}
        />
      </div>

      {activePeriods.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ciclo de Avaliação</label>
          <select
            value={selectedPeriodId}
            onChange={e => setSelectedPeriodId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
          >
            <option value="">Sem ciclo específico</option>
            {activePeriods.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Dimensões de avaliação */}
      <div className="space-y-5">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Avaliação por Dimensão</h3>
        {DIMENSIONS.map(d => (
          <div key={d.key} className="p-4 bg-gray-50 rounded-lg space-y-3">
            <p className="text-sm font-medium text-gray-800">{d.label}</p>
            <RatingScale
              value={form[d.key].rating}
              onChange={v => setForm(f => ({ ...f, [d.key]: { ...f[d.key], rating: v } }))}
              error={errors[d.key]}
            />
            <TextInput
              label="Comentário (opcional)"
              value={form[d.key].comment}
              onChange={e => setForm(f => ({ ...f, [d.key]: { ...f[d.key], comment: e.target.value } }))}
            />
          </div>
        ))}
      </div>

      {/* Feedback geral */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Feedback Geral</h3>
        <TextInput
          label="Pontos Fortes"
          value={form.strengths}
          onChange={e => setForm(f => ({ ...f, strengths: e.target.value }))}
        />
        <TextInput
          label="Pontos de Melhoria"
          value={form.improvements}
          onChange={e => setForm(f => ({ ...f, improvements: e.target.value }))}
        />
        <TextInput
          label="Plano de Ação / Recomendações"
          value={form.recommendations}
          onChange={e => setForm(f => ({ ...f, recommendations: e.target.value }))}
        />
      </div>

      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {errors.submit}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <span className="text-xs text-gray-400">
          {saveStatus === 'saving' && 'Salvando...'}
          {saveStatus === 'saved' && savedAt && `Salvo automaticamente em ${savedAt}`}
          {saveStatus === 'error' && <span className="text-red-500">Falha ao salvar rascunho</span>}
        </span>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || saveStatus === 'saving'}
            className={`px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 ${isLoading || saveStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? 'Salvando...' : 'Salvar Rascunho'}
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={isPublishing || saveStatus === 'saving' || !getDraftId()}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors ${isPublishing || saveStatus === 'saving' || !getDraftId() ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Send className="w-4 h-4" />
            {isPublishing ? 'Publicando...' : 'Publicar'}
          </button>
        </div>
      </div>
    </form>
  );
};
